/**
 * O estado do trilho (#880): recolhido, espiando e a gaveta do celular.
 *
 * Mora no widget, e quem o chama é a casca: o `<div class="casca">` marca
 * `data-recolhido`, `data-espiando` e `data-gaveta` com ele, e o véu, o menu e a
 * barra do celular dividem a mesma gaveta.
 */

import { useEffect, useRef, useState } from 'react';
import { useTelaEstreita } from '@/shared/ui/tela';
import type { SecaoDaAdministracao } from '../config/secoes-da-administracao';
import type { Area } from '../config/areas';

export type Trilho = ReturnType<typeof useTrilho>;

/**
 * `forcarRecolhido`: a tela pede o menu só com os ícones, enquanto ela está
 * aberta (o editor do mapa das automações, que precisa da largura). Não grava
 * a preferência: ao sair dela, o menu volta como a pessoa deixou.
 */
export function useTrilho(
  area: Area,
  secaoDaAdmin: SecaoDaAdministracao,
  { forcarRecolhido = false }: { forcarRecolhido?: boolean } = {},
) {
  /*
   * O menu recolhido persiste entre sessões.
   *
   * Quem recolhe o menu está pedindo mais espaço para a tela de trabalho, e
   * essa preferência não muda de um dia para o outro. Reabrir expandido a cada
   * carga obrigaria a recolher de novo toda manhã — e é o tipo de atrito que
   * faz a pessoa desistir do controle e conviver com o menu que ela não quer.
   *
   * `localStorage` e não servidor: é preferência de APARELHO, não de pessoa. A
   * mesma conta num monitor grande e num notebook quer coisas diferentes.
   */
  const [recolhido, definirRecolhido] = useState(
    () => localStorage.getItem('cliqcrm:menu-recolhido') === 'sim',
  );

  /*
   * A gaveta do celular.
   *
   * Abaixo de 720px o trilho não cabe ao lado do conteúdo, e a versão
   * anterior o deitava numa barra de oito itens que quebrava em quatro linhas
   * e comia 28% da tela antes de qualquer conteúdo (medido em 390×844). Agora
   * ele é o MESMO trilho vertical do desktop, sobreposto ao conteúdo e fechado
   * por padrão. Quem abre é o botão da barra móvel, que pertence à casca —
   * deixar cada tela desenhar o seu seria garantir que uma esquecesse.
   *
   * Fechado, o trilho é `inert`: não recebe foco nem leitura. Sem isto, quem
   * navega por Tab atravessaria oito itens invisíveis antes de chegar ao
   * conteúdo. Ao abrir, o foco entra no primeiro item; ao fechar, volta ao
   * botão que abriu. Navegar fecha, o véu fecha, o Esc fecha.
   */
  const estreita = useTelaEstreita();
  const [gavetaAberta, definirGavetaAberta] = useState(false);

  /*
   * Espiar: o menu recolhido abre ao passar o mouse, e fecha ao sair.
   * Ver o bloco de CSS "Espiar" — quem posiciona é ele; aqui só se marca.
   */
  const [espiando, definirEspiando] = useState(false);
  /*
   * Recolheu com o mouse em cima: o menu NÃO deve reabrir por cima do
   * conteúdo no mesmo instante — era o "recolhi e nada mudou". O espiar fica
   * pausado até o mouse sair do trilho uma vez.
   */
  const [espiarPausado, definirEspiarPausado] = useState(false);
  const botaoDaGaveta = useRef<HTMLButtonElement>(null);
  const trilho = useRef<HTMLElement>(null);
  const aberta = estreita && gavetaAberta;

  useEffect(() => {
    if (!aberta) return;
    trilho.current?.querySelector<HTMLElement>('button, a')?.focus();
    const aoTeclar = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') definirGavetaAberta(false);
    };
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      botaoDaGaveta.current?.focus();
    };
  }, [aberta]);

  const alternarRecolhido = (valor: boolean): void => {
    definirRecolhido(valor);
    localStorage.setItem('cliqcrm:menu-recolhido', valor ? 'sim' : 'nao');
    if (valor) {
      definirEspiando(false);
      definirEspiarPausado(true);
      // O botão que recolheu ainda tem o foco, e foco dentro do trilho abre
      // o espiar. Soltá-lo é o que deixa o menu fechado de verdade.
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }
  };

  // Navegar fecha a gaveta.
  useEffect(() => {
    definirGavetaAberta(false);
  }, [area, secaoDaAdmin]);

  /*
   * Recolhido DE FATO: no celular o menu é gaveta, e gaveta recolhida é uma
   * gaveta fechada — a preferência de trilho não vale ali. É o mesmo teste que
   * decide o `data-recolhido`, e por isso mora numa constante só.
   */
  const recolhidoDeFato = !estreita && (recolhido || forcarRecolhido);

  /*
   * Espiando, o trilho volta ao desenho aberto — inclusive no que é DOM, e não
   * só no CSS: o lockup no lugar do símbolo, o controle de recolher de volta, e
   * os nomes sem a dica do navegador. Sem isto o painel espiado mostraria o
   * símbolo de 40px ao lado de rótulos escritos, que é o desenho de nenhum dos
   * dois estados.
   */
  const mostrandoRecolhido = recolhidoDeFato && !espiando;

  return {
    estreita,
    gavetaAberta,
    definirGavetaAberta,
    aberta,
    recolhido,
    alternarRecolhido,
    espiando,
    definirEspiando,
    espiarPausado,
    definirEspiarPausado,
    botaoDaGaveta,
    trilho,
    recolhidoDeFato,
    mostrandoRecolhido,
  };
}
